import type { Datum, PlotData, TypedArray } from "plotly.js";
import type { FieldDescriptor } from "./bindings/FieldDescriptor.js";
import type { JsonValue } from "./bindings/serde_json/JsonValue.js";
import { registryMetadata } from "./metadata.js";
import type { FieldAxisMap } from "./plotConfig.js";
import type { RowKind } from "./thinning.js";

/** Attached to each trace so the table can map columns to fields */
export type PlotTraceMeta = { fieldName: string; isWrappedTrace: boolean };

/**
 * Organize plot data from backend response
 * @param tableData Data for a single table (fields indexed by field ID)
 * @param tableId Current table ID
 * @param fieldAxisMap Map of field names to axis names and colors
 * @param fieldsMetadata Field metadata from backend response
 * @param options.samplingIntervalSeconds Overrides the registry interval for gap detection (e.g. when thinned)
 * @param options.rowKinds Per-row "min"/"max" tags from thinning, attached to traces as customdata
 * @param options.hiddenFields Field names the user has hidden in the field selection panel
 */
export function organisePlotData(
	tableData: {
		[x: string]: JsonValue[];
	},
	tableId: number,
	fieldAxisMap: FieldAxisMap,
	fieldsMetadata?: Record<string, FieldDescriptor[]>,
	options: {
		samplingIntervalSeconds?: number;
		rowKinds?: RowKind[] | null;
		hiddenFields?: Set<string>;
	} = {},
): PlotData[] {
	const plotData: PlotData[] = [];
	// Timestamp is the first field (field 0)
	const TIMESTAMP_KEY = "0";

	// Build date array from timestamp field with gap detection
	const dateData: (Date | null)[] = [];
	const timestamps = tableData[TIMESTAMP_KEY] as unknown as string[];
	const samplingInterval =
		options.samplingIntervalSeconds ??
		registryMetadata[tableId]?.sampling_interval_seconds;
	const gapThreshold = samplingInterval ? samplingInterval * 1.5 * 1000 : null; // milliseconds
	const { rowKinds } = options;
	const rowKindData: (RowKind | null)[] = [];
	for (let i = 0; i < timestamps.length; i++) {
		const currentDate = new Date(timestamps[i]);

		// Check for gap if not the first element and gap detection is enabled
		if (i > 0 && gapThreshold !== null) {
			const prevDate = new Date(timestamps[i - 1]);
			const timeDiff = currentDate.getTime() - prevDate.getTime();
			if (timeDiff > gapThreshold) {
				dateData.push(null);
				rowKindData.push(null);
			}
		}

		dateData.push(currentDate);
		if (rowKinds) rowKindData.push(rowKinds[i]);
	}

	// Get field metadata for titles

	const tableIdString = tableId.toString();
	const fieldMeta = fieldsMetadata?.[tableIdString];

	// Role-based wind field detection (inspired by weatherMaps.ts)
	let windSpeedKey: string | undefined;
	let windDirectionKey: string | undefined;
	let windErrorKey: string | undefined;
	let rainKey: string | undefined;
	if (fieldMeta) {
		for (const [idx, meta] of fieldMeta.entries()) {
			if (meta.role === "wind_speed") windSpeedKey = idx.toString();
			if (meta.role === "wind_direction") windDirectionKey = idx.toString();
			if (meta.role === "wind_stdev") windErrorKey = idx.toString();
			if (meta.role === "rain_total") rainKey = idx.toString();
		}
	}

	// Second pass: process all fields for rendering
	let windspeed_mask: boolean[] | undefined;
	for (const [key, value] of Object.entries(tableData)) {
		if (key === TIMESTAMP_KEY) continue;

		const fieldIndex = Number.parseInt(key, 10);
		const fieldName = fieldMeta?.[fieldIndex]?.name ?? `Field ${key}`;

		if (options.hiddenFields?.has(fieldName)) continue;

		// Skip fields marked as not visible
		const fieldVisible = fieldMeta?.[fieldIndex]?.visible ?? true;
		if (!fieldVisible) {
			//skip processing this field entirely if it's not visible
			continue;
		}

		// Build data array with gap detection
		const dataValues: (number | null)[] = [];
		const fieldValues = value as unknown as number[];

		for (let i = 0; i < fieldValues.length; i++) {
			// Check for gap if not the first element and gap detection is enabled
			if (i > 0 && gapThreshold !== null) {
				const prevDate = new Date(timestamps[i - 1]);
				const currentDate = new Date(timestamps[i]);
				const timeDiff = currentDate.getTime() - prevDate.getTime();
				if (timeDiff > gapThreshold) {
					dataValues.push(null);
				}
			}

			dataValues.push(fieldValues[i]);
		}

		// Generate display name
		let displayName: string;
		let legendgroup: string | undefined;
		const fieldInfo: FieldDescriptor | undefined = fieldMeta?.[fieldIndex];
		const group =
			typeof fieldInfo?.group === "string" ? fieldInfo.group.trim() : "";
		const unit =
			typeof fieldInfo?.unit === "string" ? fieldInfo.unit.trim() : "";
		const groupedLegendLabel: string | undefined = group
			? unit
				? `${group} (${unit})`
				: group
			: undefined;

		if (groupedLegendLabel && fieldInfo) {
			displayName = fieldInfo.description || fieldInfo.name;
			legendgroup = groupedLegendLabel;
		} else if (fieldInfo) {
			const name = fieldInfo.description || fieldInfo.name;
			displayName = fieldInfo.unit ? `${name} (${fieldInfo.unit})` : name;
		} else {
			displayName = fieldName;
		}

		// Use role-based wind field detection
		const isWindSpeed = windSpeedKey === key;
		const isWindDirection = windDirectionKey === key;
		const isWindError = windErrorKey === key;
		const isRain = rainKey === key;
		if (isWindSpeed) {
			windspeed_mask = dataValues.map((value) => value === 0);
		}
		if (isWindDirection) windDirectionKey = key;
		if (isWindError) {
			// Skip rendering wind error fields - they're only used for error bars (already identified in first pass)
			continue;
		}
		// Find the axis and color by looking up fieldName
		const axisInfo = fieldAxisMap.get(fieldName);
		const axisName = axisInfo?.axisName ?? "y";
		const color = axisInfo?.color;

		const entry = {
			x: dateData,
			y: dataValues,
			yaxis: axisName,
			line: {
				color,
				// Rain as a filled step (hyetograph). Raw totals are stamped at the end of
				// their interval, so step back ("vh"); thinned buckets at the start ("hv").
				...(isRain && { shape: rowKinds ? "hv" : "vh" }),
			},
			...(isRain && { fill: "tozeroy" }),
			type: dataValues.length > 1000 ? "scattergl" : "scatter",
			mode: isWindDirection ? "markers" : isRain ? "lines" : "lines+markers",
			name: displayName,
			meta: { fieldName, isWrappedTrace: false } satisfies PlotTraceMeta,
			...(rowKinds && { customdata: rowKindData }),
			...(legendgroup && {
				legendgroup,
				legendgrouptitle: { text: legendgroup },
			}),
		} as unknown as PlotData;
		if (dataValues.length > 1000) {
			// debug, not warn: fires per trace on every redraw, and warn is forwarded to Sentry
			// eslint-disable-next-line no-console
			console.debug(
				`Drawing plotly gl instead of svg graph, due to large dataset: ${dataValues.length} to draw.`,
			);
		}

		// Special handling for wind direction fields
		if (isWindDirection) {
			// Add error bars if wind error field exists
			if (windErrorKey && windErrorKey in tableData) {
				const errorData: (number | null)[] = [];
				const windErrorValues = tableData[windErrorKey] as unknown as number[];

				for (let i = 0; i < windErrorValues.length; i++) {
					// Check for gap if not the first element and gap detection is enabled
					if (i > 0 && gapThreshold !== null) {
						const prevDate = new Date(timestamps[i - 1]);
						const currentDate = new Date(timestamps[i]);
						const timeDiff = currentDate.getTime() - prevDate.getTime();
						if (timeDiff > gapThreshold) {
							errorData.push(null);
						}
					}

					errorData.push(windErrorValues[i]);
				}
				entry.error_y = {
					type: "data",
					array: errorData as Datum[],
					visible: true,
				};
			}

			// Clean wind direction data (convert 0 to NaN)
			const cleanedDataValues: number[] = dataValues.map((v): number =>
				v === 0 || v === null ? NaN : (v as number),
			);
			entry.y = cleanedDataValues;
			plotData.push(entry);

			// Only add wrapped values for 360° continuity if error bars are present
			if (windErrorKey && windErrorKey in tableData) {
				const topValues = cleanedDataValues.map((value) =>
					Number.isNaN(value) ? NaN : value + 360,
				);
				const wrappedMeta: PlotTraceMeta = { fieldName, isWrappedTrace: true };
				plotData.push({
					...entry,
					meta: wrappedMeta,
					y: topValues,
					hoverinfo: "none",
					showlegend: false,
				} as unknown as PlotData);

				const bottomValues = cleanedDataValues.map((value) =>
					Number.isNaN(value) ? NaN : value - 360,
				);
				plotData.push({
					...entry,
					meta: wrappedMeta,
					y: bottomValues,
					hoverinfo: "none",
					showlegend: false,
				} as unknown as PlotData);
			}
		} else {
			plotData.push(entry);
		}
	}

	// Apply windspeed mask to wind direction if both exist
	if (windspeed_mask && windDirectionKey) {
		const applyMask = (array: TypedArray, mask: boolean[]): TypedArray => {
			return array.map((value, index) => (mask[index] ? NaN : value));
		};

		plotData.forEach((entry) => {
			if (
				entry.legendgroup &&
				/wind.*dir/i.test(entry.legendgroup as string) &&
				entry.y
			) {
				entry.y = applyMask(entry.y as TypedArray, windspeed_mask);
			}
		});
	}

	// Sort traces so any grouped traces appear last in legend.
	const groupedTraces = plotData.filter(
		(entry) =>
			typeof entry.legendgroup === "string" && entry.legendgroup.length > 0,
	);
	const ungroupedTraces = plotData.filter(
		(entry) =>
			!(typeof entry.legendgroup === "string" && entry.legendgroup.length > 0),
	);

	return [...ungroupedTraces, ...groupedTraces];
}
